import { useEffect, useState } from 'react';
import { cmsApi, SiteMenus } from '@/lib/api';

type Menus = SiteMenus['menus'];

// Module-level promise cache: Header/MainNav, MobileNav and Footer all mount
// per page, but the /menus endpoint should be hit once per page load. A
// failed fetch clears the cache so a later mount can retry.
let menusPromise: Promise<Menus | null> | null = null;

function loadMenus(): Promise<Menus | null> {
  if (!menusPromise) {
    menusPromise = cmsApi
      .getMenus()
      .then((res) => res.data?.menus ?? {})
      .catch(() => {
        menusPromise = null;
        return null;
      });
  }
  return menusPromise;
}

/**
 * Admin-managed storefront menus (Content → Site Menus). Returns null until
 * loaded (and on failure) — callers keep their hardcoded fallbacks for that
 * case so the chrome never renders empty.
 */
export function useSiteMenus(): Menus | null {
  const [menus, setMenus] = useState<Menus | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadMenus().then((m) => {
      if (!cancelled && m) setMenus(m);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return menus;
}
